class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // VAN MILE: the markup decides the state. product-form.js drops is-empty in its `finally`, even when the add
    // failed and the empty-bag markup is still here, which would show a blank filled-bag shell.
    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const isEmpty = this.classList.contains('is-empty');
        const containerToTrapFocusOn = isEmpty
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        // VAN MILE: focus lands on the heading ("Your bag (2)" / "Your bag is empty") so it is announced first
        const focusElement =
          (isEmpty ? this.querySelector('#CartDrawer-EmptyHeading') : this.querySelector('#CartDrawer-Heading')) ||
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn || document.getElementById('CartDrawer'), focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.makeOutsideInert(true);

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // VAN MILE: optional call, so opening never throws if cart-drawer-items is not (yet) upgraded.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    this.makeOutsideInert(false);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // VAN MILE: the bag can open over the open menu drawer, and only one focus trap exists at a time: give the menu
    // its trap back on the deepest open level, the way the menu sets it
    const menu = document.querySelector('#Details-menu-drawer-container[open]');
    if (menu) {
      const levels = menu.querySelectorAll('details[open] > .menu-drawer__submenu');
      const level = levels[levels.length - 1];
      if (level) trapFocus(level, level.querySelector('button'));
      else trapFocus(menu, menu.querySelector('summary'));
    }
  }

  // VAN MILE: Safari tabs only to form controls by default, so it skips the trap's first/last links and focus
  // left the open dialog (and Escape, listened for on the drawer, stopped working). While the bag is open
  // everything outside it is inert, and Escape is also handled at document level.
  makeOutsideInert(on) {
    if (on) {
      if (this.inertNodes) return;
      this.inertNodes = [];
      for (let node = this; node && node !== document.body; node = node.parentElement) {
        for (const sibling of node.parentElement ? node.parentElement.children : []) {
          if (sibling === node || sibling.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) continue;
          sibling.inert = true;
          this.inertNodes.push(sibling);
        }
      }
      this.onDocumentKeydown = (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        // the keyup of this Escape lands on the menu control close() just focused (bag opened over the open menu):
        // swallow it, or the menu would close a level too
        const swallow = (e) => {
          if (e.key !== 'Escape') return;
          e.stopPropagation();
          window.removeEventListener('keyup', swallow, true);
        };
        window.addEventListener('keyup', swallow, true);
      };
      document.addEventListener('keydown', this.onDocumentKeydown);
    } else {
      (this.inertNodes || []).forEach((el) => (el.inert = false));
      this.inertNodes = null;
      if (this.onDocumentKeydown) document.removeEventListener('keydown', this.onDocumentKeydown);
      this.onDocumentKeydown = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Called after an add to cart (product form, quick add) with the Section Rendering API response.
  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    this.showAddedState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // "Added to your bag" replaces the heading for 2 s, then "Your bag (n)" returns (visual only; the heading text stays for screen readers).
  showAddedState() {
    const header = this.querySelector('#CartDrawer-Header');
    if (!header || this.classList.contains('is-empty')) return;
    clearTimeout(this.addedTimer);
    header.classList.add('is-added');
    this.addedTimer = setTimeout(() => {
      this.querySelector('#CartDrawer-Header')?.classList.remove('is-added');
    }, 2000);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
